import { useState } from 'react';
import PropTypes from 'prop-types';

import { Flex, Spacer } from '@zillow/constellation';
import { Button, Paragraph, Tag, Text, TextButton } from '@zillow/constellation-10';
import { IconChevronDownCircleFilled, IconChevronUpCircleFilled } from '@zillow/constellation-icons';
import { LoadingText } from '@zg-rentals/loading-components';

import { IconCheck, IconError, IconifiedList, IconRadioDeselected } from '@zillow/zrs-comet';

import { getTagTypeByKey, STATUS_KEY } from '../../../../utils/statusTagUtils';

import { StyledFlex } from './StyledComponents';
import InlineStyledText from '../InlineStyledText';

const { PAYMENT_SUMMARY } = STATUS_KEY;

const LIST_FONT_MAP = {
  normal: {
    textStyle: 'body-sm',
    color: 'text.subtle',
  },
  emphasize: {
    textStyle: 'body-sm-bold',
    color: 'text.subtle',
  },
  lowlight: {
    textStyle: 'body-sm',
    color: 'text.subtle',
  },
};

const LIST_ICON_MAP = {
  checked: <IconCheck fontColor="green300" />,
  unchecked: <IconRadioDeselected fontColor="borderLight" />,
  alert: <IconError fontColor="red400" />,
};

const CTA_BUTTON_MAP = {
  primary_button: 'filled',
  secondary_button: 'outlined',
};

const PaymentTracker = ({ isLoading, statusText, statusType, trackerStates, ctaButtons, disclaimer }) => {
  const [isDropdownEnabled, setDropdownEnabled] = useState(false);

  const getIconifiedListItems = (list) =>
    list.map(({ messageText, icon, messageStyle }) => ({
      icon: LIST_ICON_MAP[icon],
      primaryContent: <Text {...LIST_FONT_MAP[messageStyle]}>{messageText}</Text>,
    }));

  return (
    <div className="PaymentTracker" data-test="connected-payments-payment-tracker">
      <Flex
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        paddingBottom={isDropdownEnabled ? 'md' : null}
      >
        {isLoading ? (
          <>
            <LoadingText textStyle="body-sm" loadingWidth="30%" isLoading={true} />
            <LoadingText textStyle="body-sm-bold" loadingWidth="30%" isLoading={true} />
          </>
        ) : (
          <>
            <Tag size="sm" tone={getTagTypeByKey(PAYMENT_SUMMARY, statusType)}>
              {statusText}
            </Tag>
            <TextButton
              onClick={() => setDropdownEnabled(!isDropdownEnabled)}
              textStyle="body-sm-bold"
              data-test="payment-tracker-dropdown-toggle"
              icon={isDropdownEnabled ? <IconChevronUpCircleFilled /> : <IconChevronDownCircleFilled />}
              iconPosition="end"
            >
              View details
            </TextButton>
          </>
        )}
      </Flex>
      {isDropdownEnabled && (
        <div data-test="payment-tracker-dropdown">
          {trackerStates.length ? (
            <Spacer paddingBottom="xs">
              <IconifiedList
                backgroundColor="white"
                verticalLineColor="borderLight"
                iconSize="md"
                listItems={getIconifiedListItems(trackerStates)}
              />
            </Spacer>
          ) : null}
          {disclaimer ? (
            <Paragraph textStyle="body-sm" color="text.subtle" css={{ paddingBottom: ctaButtons.length ? '600' : 0 }}>
              <InlineStyledText fontColor="text.subtle" styledTextList={disclaimer} />
            </Paragraph>
          ) : null}
          {ctaButtons.length ? (
            <StyledFlex display="flex">
              {ctaButtons.map(({ text, styling, url }) => (
                <Flex key={url} className="PaymentTracker-CTA-Button" flex="1">
                  <Button
                    asChild
                    fluid={true}
                    size="sm"
                    tone="brand"
                    emphasis={CTA_BUTTON_MAP[styling]}
                    textStyle="body-sm-bold"
                    css={{ whiteSpace: 'nowrap' }}
                  >
                    <a href={url}>{text}</a>
                  </Button>
                </Flex>
              ))}
            </StyledFlex>
          ) : null}
        </div>
      )}
    </div>
  );
};

PaymentTracker.propTypes = {
  paymentType: PropTypes.string.isRequired,
  statusText: PropTypes.string.isRequired,
  statusType: PropTypes.string.isRequired,
  trackerStates: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.oneOf(['checked', 'unchecked', 'alert']).isRequired,
      message: PropTypes.string.isRequired,
      styling: PropTypes.oneOf(['normal', 'emphasize', 'lowLight', 'emphasize']),
    }),
  ).isRequired,
  ctaButtons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      styling: PropTypes.string.isRequired,
    }),
  ),
  disclaimer: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      styling: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
};

PaymentTracker.defaultProps = {
  ctaButtons: [],
  disclaimer: [],
  isLoading: false,
};

export default PaymentTracker;
